import React, {createContext, useContext, useState, useEffect} from 'react'

const GlobalPreviewContext = createContext(null)

// Module-level store for preview content - allows communication between
// Preview component and GlobalElementsProvider even when they're not in
// the same context tree (Preview is a child of GlobalElementsProvider in _app)
let globalPreviewStore = {
    content: null,
    listeners: new Set()
}

const setGlobalPreviewContent = (content) => {
    globalPreviewStore.content = content
    // Notify all listeners
    globalPreviewStore.listeners.forEach(listener => {
        listener(content)
    })
}

const subscribeToGlobalPreview = (listener) => {
    globalPreviewStore.listeners.add(listener)
    return () => {
        globalPreviewStore.listeners.delete(listener)
    }
}

/**
 * GlobalPreviewProvider
 *
 * A special provider for preview mode that provides preview content to GlobalElementsProvider.
 * When editing global components (Header, MegaMenu, MainShopMenu, etc.) in Optimizely CMS,
 * this provider makes the preview content available to be merged into global elements.
 *
 * Uses a module-level store to communicate with GlobalElementsProvider which is higher
 * in the component tree (in _app).
 */
export const GlobalPreviewProvider = ({children, previewContent}) => {
    // Set the preview content in the module-level store
    useEffect(() => {
        setGlobalPreviewContent(previewContent)
        return () => {
            setGlobalPreviewContent(null)
        }
    }, [previewContent])

    return (
        <GlobalPreviewContext.Provider value={previewContent}>
            {children}
        </GlobalPreviewContext.Provider>
    )
}

/**
 * Hook to get the global preview content (if any)
 * Uses the module-level store to get content from GlobalPreviewProvider
 * even when called from a parent component (like GlobalElementsProvider)
 */
export const useGlobalPreviewContent = () => {
    const [previewContent, setPreviewContent] = useState(globalPreviewStore.content)

    useEffect(() => {
        // Check current value immediately (in case content was set before we subscribed)
        if (globalPreviewStore.content !== previewContent) {
            setPreviewContent(globalPreviewStore.content)
        }

        // Subscribe to future changes in the global preview store
        const unsubscribe = subscribeToGlobalPreview((content) => {
            setPreviewContent(content)
        })
        return unsubscribe
    }, [])

    // Also try context (for nested usage)
    const contextContent = useContext(GlobalPreviewContext)

    return previewContent || contextContent
}

/**
 * Collect all ContentLink.GuidValue -> content mappings from preview content
 * This handles nested structures like GlobalComponentPage which contains HeaderReference, etc.
 */
const collectPreviewItems = (obj, map = new Map()) => {
    if (!obj || typeof obj !== 'object') return map

    // If this object has a ContentLink with GuidValue, store it
    if (obj?.ContentLink?.GuidValue) {
        map.set(obj.ContentLink.GuidValue, obj)
    }

    // Also check for direct GuidValue with Expanded (reference pattern)
    if (obj?.GuidValue && obj?.Expanded) {
        map.set(obj.GuidValue, obj.Expanded)
    }

    // Recursively process arrays
    if (Array.isArray(obj)) {
        obj.forEach((item) => collectPreviewItems(item, map))
        return map
    }

    // Recursively process object properties
    for (const key of Object.keys(obj)) {
        collectPreviewItems(obj[key], map)
    }

    return map
}

/**
 * Deep merge preview content into global content
 * Finds blocks by ContentLink.GuidValue and replaces with preview version
 * Handles nested structures like GlobalComponentPage containing HeaderReference
 */
export const mergePreviewIntoGlobal = (globalContent, previewContent) => {
    if (!globalContent || !previewContent?.items?.[0]) {
        return globalContent
    }

    const previewItem = previewContent.items[0]

    // Collect all preview items (including nested ones) by their GuidValue
    const previewMap = collectPreviewItems(previewItem)

    if (previewMap.size === 0) {
        return globalContent
    }

    // Helper to recursively find and replace content by GuidValue
    const replaceContent = (obj) => {
        if (!obj || typeof obj !== 'object') return obj

        // Check if this object has a ContentLink.GuidValue that matches any preview item
        if (obj?.ContentLink?.GuidValue && previewMap.has(obj.ContentLink.GuidValue)) {
            const previewData = previewMap.get(obj.ContentLink.GuidValue)
            return {
                ...obj,
                ContentLink: {
                    ...obj.ContentLink,
                    Expanded: previewData?.ContentLink?.Expanded || previewData
                }
            }
        }

        // Check if this is a reference with matching GuidValue
        if (obj?.GuidValue && previewMap.has(obj.GuidValue) && obj?.Expanded) {
            const previewData = previewMap.get(obj.GuidValue)
            return {
                ...obj,
                Expanded: previewData?.Expanded || previewData
            }
        }

        // Recursively process arrays
        if (Array.isArray(obj)) {
            return obj.map((item) => replaceContent(item))
        }

        // Recursively process object properties
        const result = {}
        for (const key of Object.keys(obj)) {
            result[key] = replaceContent(obj[key])
        }
        return result
    }

    return replaceContent(globalContent)
}
